/**
 * The cadence model a schedule-triggered rule stores, web-side.
 *
 * Canonical shape: `{ every, unit, time?, daysOfWeek?, dayOfMonth?, timezone }`
 * — "every 2 weeks", "every 6 hours", "every 1 year". It mirrors
 * `utils/automations/schedule.js` in the API, deliberately and only as far as
 * READING goes: the numbers here drive an editor and a sentence, never
 * recurrence math, which stays server-side where the timezone database lives.
 *
 * `anchor` is the one field this side never touches. The API stamps it at
 * creation and at a genuine cadence change (it is what makes "every 2 weeks"
 * mean a specific fortnight rather than drifting with whenever the ticker ran),
 * and a client that echoed a stale one back — or invented a fresh one on a
 * no-op save — would silently re-phase somebody's reminder. So the editor
 * builds the cadence from scratch and omits it; the server owns it.
 *
 * LEGACY `freq` shapes (`daily`/`weekly`/`monthly`/`interval`) predate the unit
 * model and are still stored on rules nobody has edited since. They normalize
 * on READ here exactly as they do server-side, so an old rule opens in the new
 * editor showing what it actually does.
 */

export const SCHEDULE_UNITS = [ 'minute', 'hour', 'day', 'week', 'month', 'year' ];

export const SCHEDULE_UNIT_OPTIONS = [
	{ value: 'minute', label: 'Minute(s)' },
	{ value: 'hour', label: 'Hour(s)' },
	{ value: 'day', label: 'Day(s)' },
	{ value: 'week', label: 'Week(s)' },
	{ value: 'month', label: 'Month(s)' },
	{ value: 'year', label: 'Year(s)' },
];

/** The API's own per-unit ceiling, so the input refuses before the round trip. */
export const EVERY_MAX = { minute: 1440, hour: 168, day: 365, week: 52, month: 24, year: 10 };

/** Minute and hour cadences have no wall-clock time — they step from the anchor. */
export const unitIsTimed = unit => unit !== 'minute' && unit !== 'hour';

/**
 * Any stored schedule -> the canonical unit shape. Unknown/absent shapes fall
 * back to daily, which is what the API's normalizer does too.
 */
export function normalizeSchedule( schedule ) {
	const s = schedule || {};
	if ( s.unit ) return { ...s, every: Number.isFinite( Number( s.every ) ) ? Number( s.every ) : 1 };
	if ( s.freq === 'interval' ) {
		return { ...s, every: Number.isFinite( Number( s.everyMinutes ) ) ? Number( s.everyMinutes ) : 60, unit: 'minute' };
	}
	if ( s.freq === 'weekly' ) return { ...s, every: 1, unit: 'week' };
	if ( s.freq === 'monthly' ) return { ...s, every: 1, unit: 'month' };
	return { ...s, every: 1, unit: 'day' };
}

/* -------------------------------------------------------------- rolling window
 *
 * `check.window = {type:'rolling', minutes}` — MINUTES is the storage unit and
 * stays that way; the API compares against it and nothing here re-teaches that.
 * What changed is the question asked: "1440" is not how anyone says "a day", so
 * the editor asks for a quantity and a unit and multiplies. The split back is
 * deliberately COARSEST-FIRST within days, so a stored 10080 re-opens as "7
 * days" — the number the author typed — rather than as 168 hours.
 *
 * Weeks are offered as an INPUT unit only. A stored 10080 could equally read as
 * 1 week, but "7 days" is what a reminder is actually about, and a round-trip
 * that silently renamed the author's own number would be its own small lie.
 */

export const WINDOW_MINUTES_MIN = 1;

/** The API's ceiling: 90 days. */
export const WINDOW_MINUTES_MAX = 129600;

export const WINDOW_UNIT_MINUTES = { minute: 1, hour: 60, day: 1440, week: 10080 };

export const WINDOW_UNIT_OPTIONS = [
	{ value: 'minute', label: 'Minute(s)' },
	{ value: 'hour', label: 'Hour(s)' },
	{ value: 'day', label: 'Day(s)' },
	{ value: 'week', label: 'Week(s)' },
];

/** The largest quantity of a unit that still fits inside the API's ceiling. */
export const windowQtyMax = unit => Math.floor( WINDOW_MINUTES_MAX / ( WINDOW_UNIT_MINUTES[ unit ] || 1 ) );

/** quantity + unit -> canonical minutes, clamped to what the API accepts. */
export function windowMinutesFrom( qty, unit ) {
	const per = WINDOW_UNIT_MINUTES[ unit ] || 1;
	const raw = Math.round( ( Number( qty ) || 0 ) * per );
	if ( ! Number.isFinite( raw ) || raw < WINDOW_MINUTES_MIN ) return WINDOW_MINUTES_MIN;
	return Math.min( raw, WINDOW_MINUTES_MAX );
}

/** Canonical minutes -> `{qty, unit}`, largest evenly-dividing unit up to days. */
export function splitWindowMinutes( minutes ) {
	const total = Number( minutes );
	if ( ! Number.isFinite( total ) || total < WINDOW_MINUTES_MIN ) {
		return { qty: 60, unit: 'minute' };
	}
	const clamped = Math.min( Math.round( total ), WINDOW_MINUTES_MAX );
	if ( clamped % WINDOW_UNIT_MINUTES.day === 0 ) {
		return { qty: clamped / WINDOW_UNIT_MINUTES.day, unit: 'day' };
	}
	if ( clamped % WINDOW_UNIT_MINUTES.hour === 0 ) {
		return { qty: clamped / WINDOW_UNIT_MINUTES.hour, unit: 'hour' };
	}
	return { qty: clamped, unit: 'minute' };
}

/* ------------------------------------------------------------ in a sentence */

const joinAnd = words => {
	if ( words.length <= 1 ) return words[ 0 ] || '';
	return `${ words.slice( 0, -1 ).join( ', ' ) } and ${ words[ words.length - 1 ] }`;
};

const WEEKDAY_NAMES = [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ];

/**
 * A schedule's cadence in a sentence — shared by the automations list and the
 * exports list, which store the same shape — spoken in the quantity+unit model the
 * rules actually store: "Every 2 weeks on Monday and Friday at 08:00 (UTC)",
 * "Every 6 hours", "Every year on day 29 at 09:00".
 *
 * Legacy `freq` shapes normalize first, so a rule nobody has edited since the
 * unit model landed still summarizes correctly rather than falling through to a
 * generic sentence.
 *
 * The timezone is named out loud because it is the one field on a rule nothing
 * else in the platform carries — a reminder that fires "at 09:00" is
 * meaningless until you know whose 09:00. Minute and hour cadences quote no
 * zone: they step from an anchor and never touch a wall clock.
 */
export function describeSchedule( rawSchedule ) {
	const schedule = normalizeSchedule( rawSchedule );
	const every = Number.isFinite( schedule.every ) && schedule.every > 0 ? schedule.every : 1;
	const unit = schedule.unit;
	const period = every === 1 ? `Every ${ unit }` : `Every ${ every } ${ unit }s`;

	if ( ! unitIsTimed( unit ) ) return period;

	// Several times a day is ONE cadence; the sentence says so rather than
	// making a manager open the editor to find out.
	const slots = Array.isArray( schedule.times ) && schedule.times.length ? schedule.times : null;
	const time = slots ? joinAnd( slots ) : ( schedule.time || '09:00' );
	const zone = schedule.timezone ? ` (${ schedule.timezone })` : '';

	if ( unit === 'week' ) {
		const days = ( Array.isArray( schedule.daysOfWeek ) ? schedule.daysOfWeek : [] )
			.map( day => WEEKDAY_NAMES[ day ] )
			.filter( Boolean );
		if ( ! days.length ) return `${ period } at ${ time }${ zone }`;
		// "Every Monday and Friday" says the same thing as "Every week on Monday
		// and Friday" in half the words; the multiple is only worth naming when
		// there IS one.
		const when = every === 1 ? `Every ${ joinAnd( days ) }` : `${ period } on ${ joinAnd( days ) }`;
		return `${ when } at ${ time }${ zone }`;
	}

	if ( ( unit === 'month' || unit === 'year' ) && Number.isFinite( Number( schedule.dayOfMonth ) ) ) {
		return `${ period } on day ${ Number( schedule.dayOfMonth ) } at ${ time }${ zone }`;
	}

	return `${ period } at ${ time }${ zone }`;
}
